import { z } from 'zod'

export const QuoteStatus = z.enum([
  'draft',
  'in_review',
  'approved',
  'sent',
  'accepted',
  'rejected',
])
export type QuoteStatus = z.infer<typeof QuoteStatus>

/**
 * The status lattice the UI is allowed to walk. Deliberately permissive in the
 * backwards direction — a quote sent by mistake has to be able to go back to
 * draft — but 'accepted'/'rejected' are terminal from the customer's side.
 */
export const QUOTE_STATUS_TRANSITIONS: Record<QuoteStatus, QuoteStatus[]> = {
  draft: ['in_review', 'approved', 'sent'],
  in_review: ['draft', 'approved', 'sent'],
  approved: ['draft', 'in_review', 'sent'],
  sent: ['accepted', 'rejected', 'draft'],
  accepted: ['sent'],
  rejected: ['sent', 'draft'],
}

export function canTransition(from: QuoteStatus, to: QuoteStatus): boolean {
  return QUOTE_STATUS_TRANSITIONS[from].includes(to)
}

export const QuoteLineItemSchema = z.object({
  id: z.string().uuid(),
  quote_id: z.string().uuid(),
  organization_id: z.string().uuid(),
  position: z.number().int(),
  description: z.string(),
  detail: z.string().nullable(),
  quantity: z.number(),
  unit: z.string(),
  unit_price_cents: z.number().int(),
  total_cents: z.number().int(),
  created_at: z.string(),
})
export type QuoteLineItem = z.infer<typeof QuoteLineItemSchema>

export const QuoteSchema = z.object({
  id: z.string().uuid(),
  organization_id: z.string().uuid(),
  created_by: z.string().uuid(),
  quote_number: z.string(),
  title: z.string(),
  customer_name: z.string(),
  customer_company: z.string().nullable(),
  customer_email: z.string().nullable(),
  status: QuoteStatus,
  currency: z.string().length(3),
  brief: z.string(),
  summary: z.string().nullable(),
  notes: z.string().nullable(),
  terms: z.string().nullable(),
  assumptions: z.array(z.string()),
  subtotal_cents: z.number().int(),
  discount_cents: z.number().int(),
  tax_rate: z.number(),
  tax_cents: z.number().int(),
  total_cents: z.number().int(),
  valid_until: z.string().nullable(),
  sent_at: z.string().nullable(),
  decided_at: z.string().nullable(),
  source_document_ids: z.array(z.string().uuid()),
  created_at: z.string(),
  updated_at: z.string(),
})
export type Quote = z.infer<typeof QuoteSchema>

export type QuoteWithLineItems = Quote & { line_items: QuoteLineItem[] }

/** Request body for the quote-generate edge function. */
export const GenerateQuoteInputSchema = z.object({
  brief: z.string().min(15, 'Describe the job in a little more detail').max(8000),
  title: z.string().max(200).optional(),
  customerName: z.string().max(200).optional(),
  customerCompany: z.string().max(200).optional(),
  customerEmail: z.string().email().or(z.literal('')).optional(),
  currency: z.string().length(3).optional(),
  documentIds: z.array(z.string().uuid()).optional(),
  matchCount: z.number().int().min(1).max(30).optional(),
})
export type GenerateQuoteInput = z.infer<typeof GenerateQuoteInputSchema>

export const GenerateQuoteResultSchema = z.object({
  quoteId: z.string().uuid(),
  quoteNumber: z.string(),
  confidence: z.enum(['high', 'medium', 'low']),
  assumptions: z.array(z.string()),
  retrievedCount: z.number().int(),
  sourceDocumentIds: z.array(z.string().uuid()),
})
export type GenerateQuoteResult = z.infer<typeof GenerateQuoteResultSchema>
